/**
 * The only module in the application that performs HTTP.
 *
 * ADR-0003: *"API access only through the generated client (`web/src/api/gen`).
 * A hand-written `fetch` against `/v1` is a CI failure."* `eslint.config.js`
 * enforces that by banning `fetch`, `XMLHttpRequest` and any `/v1` string
 * literal everywhere in `src/` except this file.
 *
 * # What this file adds on top of the generated types
 *
 * `openapi-fetch` turns `paths` from the generated schema into a typed client.
 * It returns `{ data, error }` rather than throwing, which is a reasonable
 * default and the wrong one for TanStack Query, whose retry, error boundary and
 * `isError` all key off a thrown value. So each verb is wrapped once here to
 * throw `ApiError`, and the rest of the application never sees a `{ data, error }`
 * tuple.
 *
 * # Credentials
 *
 * `credentials: 'same-origin'`, because the session is an `HttpOnly` cookie set
 * by `POST /v1/auth/sign-in` and the browser sends it only when asked. It is
 * `same-origin` rather than `include` on purpose: the API refuses a
 * cookie-authenticated write that is not same-origin anyway
 * (`internal/api/auth.go`, `requireSameOriginHeaders`), so `include` would only
 * widen what this client is willing to send a credential to without widening
 * what the server accepts.
 *
 * # The CSRF question, answered from the implementation
 *
 * `docs/api/openapi/v1.yaml` documents a double-submit token in `X-ExiGit-CSRF`
 * and a cookie named `__Host-exigit_session`. Neither exists. The server
 * (`internal/api/auth.go`) defends cookie-authenticated writes by requiring
 * `Origin` or `Sec-Fetch-Site` to name this site — headers the browser sets and
 * page script cannot forge — and the cookie is named by `SESSION_COOKIE_NAME`,
 * default `exigit_session`. This client is built against the implementation and
 * sends no CSRF header, because nothing reads one. The divergence is recorded
 * in web/README.md and in docs/RISKS_AND_OPEN_QUESTIONS.md; it is not worked
 * around here and the contract is not edited from the frontend.
 *
 * The practical consequence is that the application must be served from the
 * same origin as the API. In development that is the Vite proxy in
 * `vite.config.ts`; in production it is CloudFront in front of both.
 */

import createClient, { type Middleware } from 'openapi-fetch';

import type { paths } from './gen/schema';
import { ApiError, isProblem, syntheticProblem, type Problem } from './problem';

/**
 * `/v1` on the page's own origin, resolved rather than left relative.
 *
 * Not configurable at runtime. A build-time switch for "point the SPA at a
 * different API" is the feature that makes a cross-origin deployment possible,
 * and a cross-origin deployment is the one the server refuses.
 *
 * It is resolved against `location.origin` rather than left as the string
 * `/v1` for two reasons: it says out loud that the API is wherever this page
 * came from, and `new Request('/v1/…')` throws outside a browser, which made
 * the middleware below impossible to test.
 */
const BASE_URL =
  typeof window === 'undefined' ? '/v1' : new URL('/v1', window.location.origin).toString();

const client = createClient<paths>({
  baseUrl: BASE_URL,
  credentials: 'same-origin',
  headers: {
    Accept: 'application/json, application/problem+json',
  },
  // Looked up per call rather than captured. `openapi-fetch` otherwise binds
  // whatever `globalThis.fetch` was when this module loaded, which makes the
  // middleware below untestable — a stubbed fetch is installed after import
  // and would never be reached. The indirection costs one property lookup and
  // changes nothing in a browser.
  fetch: (request) => globalThis.fetch(request),
});

/* ---------------------------------------------------------------------- *
 * The unauthenticated signal
 *
 * A 401 can arrive on *any* request, not only on the session probe: a session
 * expires, or an operator revokes it, while the user is looking at a page. The
 * API layer cannot resolve that — it must not know about the session, the
 * query cache or the router — so it announces it and the session layer acts.
 *
 * Before this existed, a real expired session (verified against a running
 * server) left the user inside the authenticated shell, with their own avatar
 * still in the top bar, reading "Could not load activity — Authentication is
 * required" and never being returned to sign-in. That is the ambiguous state
 * this closes.
 * ---------------------------------------------------------------------- */

type UnauthenticatedListener = () => void;

const unauthenticatedListeners = new Set<UnauthenticatedListener>();

/**
 * Be told when the server refuses a request for want of a valid session.
 *
 * Returns the unsubscribe. The listener may fire several times in quick
 * succession — every in-flight query on a page fails at once — so it must be
 * idempotent; `SessionProvider`'s is, because the second call finds the
 * session already anonymous and does nothing.
 */
export function onUnauthenticated(listener: UnauthenticatedListener): () => void {
  unauthenticatedListeners.add(listener);
  return () => {
    unauthenticatedListeners.delete(listener);
  };
}

/**
 * Turn a non-2xx response into an `ApiError` before `openapi-fetch` reaches its
 * own error path, so that every failure in the application has one shape and
 * carries the trace id.
 */
const raiseProblems: Middleware = {
  async onResponse({ response }) {
    if (response.ok) return response;

    const retryAfter = Number.parseInt(response.headers.get('Retry-After') ?? '', 10);
    // Set by the server when the rate limiter could not reach its counter
    // store. A `FailClosed` policy then refuses everyone, which is a different
    // thing to tell the user than "you tried too often".
    const degraded = response.headers.get('RateLimit-Degraded') === 'true';
    let problem: Problem;

    try {
      const body: unknown = await response.clone().json();
      problem = isProblem(body)
        ? body
        : syntheticProblem(
            response.status,
            `The server answered ${String(response.status)} with a body this client did not recognise.`,
          );
    } catch {
      // A body that is not JSON at all: an upstream proxy's error page, or an
      // empty body on a 502. `statusText` is often empty over HTTP/2, so it is
      // not used as the message.
      problem = syntheticProblem(
        response.status,
        `The server answered ${String(response.status)} with no readable body.`,
      );
    }

    if (response.status === 401) {
      // Announced for every 401, including the session probe's own. The
      // listener is responsible for not looping: the probe resolving to
      // `anonymous` is already the end state, so a second notification about
      // it is a no-op.
      for (const listener of unauthenticatedListeners) listener();
    }

    throw new ApiError(problem, {
      ...(Number.isFinite(retryAfter) ? { retryAfterSeconds: retryAfter } : {}),
      ...(degraded ? { isDegraded: true } : {}),
    });
  },
};

client.use(raiseProblems);

/**
 * The typed client itself.
 *
 * Exported rather than re-wrapped verb by verb. A generic passthrough around
 * `GET`/`POST`/… has to reconstruct `openapi-fetch`'s parameter types through
 * conditional types, which compiles until the library changes shape and then
 * fails with an error nobody can read. `operations.ts` names one function per
 * endpoint instead, which is more lines and is the layer where a path, its
 * parameters and its return type are stated once and checked against the
 * contract.
 *
 * Only `operations.ts` and `client.test.ts` import this.
 */
export const http = client;

/**
 * Resolve an `openapi-fetch` result to its body, or throw.
 *
 * The middleware above throws on every HTTP failure, so the `error` branch here
 * catches only what never reached `onResponse`: a transport failure, an abort,
 * a request the browser refused to make. That is why the `ApiError` it builds
 * carries `isTransport` — the caller's decision between "retry" and "tell the
 * user they are signed out" turns on exactly that distinction.
 */
export async function expect<Data>(call: Promise<{ data?: Data; error?: unknown }>): Promise<Data> {
  let result: { data?: Data; error?: unknown };
  try {
    result = await call;
  } catch (cause) {
    if (cause instanceof ApiError) throw cause;
    throw new ApiError(
      syntheticProblem(0, cause instanceof Error ? cause.message : 'The request did not complete.'),
      { isTransport: true },
    );
  }

  if (result.error !== undefined) {
    throw result.error instanceof ApiError
      ? result.error
      : new ApiError(syntheticProblem(0, 'The request did not complete.'), { isTransport: true });
  }

  return result.data as Data;
}

/**
 * The same, for the operations that answer `204 No Content`.
 *
 * Separate from `expect` so that a `204` reads as a deliberate "there is no
 * body" at the call site rather than as a `Data` that happens to be undefined
 * and will be dereferenced two lines later.
 */
export async function expectNoContent(call: Promise<{ error?: unknown }>): Promise<void> {
  await expect(call as Promise<{ data?: undefined; error?: unknown }>);
}

/**
 * The operational probes, which sit outside `/v1` in `internal/api/api.go` and
 * are therefore not in the OpenAPI document and not in the generated client.
 *
 * This is the one deliberate exception to "every call goes through the
 * generated client", and it is narrow on purpose: two fixed paths, GET only, no
 * credentials, no body read. It exists so the application can tell "the API is
 * down" from "you are signed out", which are the two explanations for a wall of
 * 401s and lead to opposite user actions.
 */
export async function probeReadiness(signal?: AbortSignal): Promise<boolean> {
  try {
    const response = await fetch('/readyz', {
      method: 'GET',
      credentials: 'omit',
      cache: 'no-store',
      ...(signal ? { signal } : {}),
    });
    return response.ok;
  } catch {
    return false;
  }
}
